import { t } from "i18next";
import moment from "moment";
import { PERMISSIONS, useUser } from "../../core/context/UserContext";
import { Select, Tag, Button, Popconfirm, Spin } from "antd";
import { DeleteOutlined } from "@ant-design/icons";
import WorklistItemStatusEnum, { getStatusLabel, getStatusOptions } from "../../enums/WorklistItemStatusEnum";
import ApiService from "../../core/service/api-service";
import { useState, useEffect } from "react";
import { useModal } from "../../core/context/ModalContext";

export default function AppointmentDetails({appointmentId, onStatusChange, onRemove}) {
    const { hasPermission } = useUser();
    const [appointment, setAppointment] = useState(null);
    const [loading, setLoading] = useState(false);
    const worklistItems = appointment?.worklistItems || [];
    const {closeModal} = useModal();

    useEffect(() => {
        if (appointmentId) {
            loadAppointment();
        }
    }, [appointmentId]);

    const loadAppointment = async () => {
        setLoading(true);
        try {
            const response = await ApiService.Appointment.GetById(appointmentId);
            if (response.success) {
                setAppointment(response.data);
            }
        } catch (error) {
            console.error('Failed to load appointment:', error);
        } finally {
            setLoading(false);
        }
    };

    const handleRemove = async (worklistItemId) => {
        try {
            const response = await ApiService.Appointment.UpdateStatus({
                id: appointment.id,
                worklistItemId: worklistItemId,
                status: 0
            });

            if (response.success) {
                if (onRemove) {
                    onRemove();
                }

                if(worklistItems.length === 1) {
                    closeModal();
                    return
                }

                await loadAppointment();
            }
        } catch (error) {
            console.error('Failed to remove item from appointment:', error);
        }
    };

    const handleStatusChangeInternal = async (newStatus) => {
        try {
            const response = await ApiService.Appointment.UpdateStatus({
                id: appointment.id,
                status: newStatus
            });

            if (response.success) {
                if(newStatus === WorklistItemStatusEnum.UNSCHEDULED.value) {
                    if (onStatusChange) {
                        await onStatusChange(newStatus);
                    }
                    return
                }
                else {
                    if (onStatusChange) {
                        await onStatusChange(newStatus);
                    }
                    // Status değiştikten sonra appointment'i yeniden yükle
                    await loadAppointment();
                }
            }
        } catch (error) {
            console.error('Failed to update appointment status:', error);
        }
    };

    if (loading) {
        return <Spin />;
    }

    if (!appointment) {
        return null;
    }

    return (
        <div style={{ display: 'flex', alignItems: 'start', gap: 16 }}>
            <table style={{ borderCollapse: 'collapse', flex: 1 }}>
                <tbody>
                    <tr>
                        <td style={{ padding: '8px', borderBottom: '1px solid #ddd', fontWeight: 'bold' }}>{t('Device')}:</td>
                        <td style={{ padding: '8px', borderBottom: '1px solid #ddd' }}>{appointment.device?.name} ({appointment.device?.aeTitle})</td>
                    </tr>
                    <tr>
                        <td style={{ padding: '8px', borderBottom: '1px solid #ddd', fontWeight: 'bold' }}>{t('Patient')}:</td>
                        <td style={{ padding: '8px', borderBottom: '1px solid #ddd' }}>
                            {worklistItems?.[0]?.patientName} {worklistItems?.[0]?.patientLastname}
                        </td>
                    </tr>
                    <tr>
                        <td style={{ padding: '8px', borderBottom: '1px solid #ddd', fontWeight: 'bold' }}>{t('ScheduledStartTime')}:</td>
                        <td style={{ padding: '8px', borderBottom: '1px solid #ddd' }}>{moment(appointment.scheduledStartTime).format('DD.MM.YYYY HH:mm')}</td>
                    </tr>
                    <tr>
                        <td style={{ padding: '8px', borderBottom: '1px solid #ddd', fontWeight: 'bold' }}>{t('ScheduledEndTime')}:</td>
                        <td style={{ padding: '8px', borderBottom: '1px solid #ddd' }}>{moment(appointment.scheduledEndTime).format('DD.MM.YYYY HH:mm')}</td>
                    </tr>
                    <tr>
                        <td style={{ padding: '8px', borderBottom: '1px solid #ddd', fontWeight: 'bold' }}>{t('Status')}:</td>
                        <td style={{ padding: '8px', borderBottom: '1px solid #ddd' }}>
                            {hasPermission(PERMISSIONS.WORKLIST_STATUS_CHANGE) ? (
                                <Select
                                    value={appointment.status}
                                    onChange={handleStatusChangeInternal}
                                    options={getStatusOptions()}
                                    style={{ width: 200 }}
                                />
                            ) : (
                                <span>{getStatusLabel(appointment.status)}</span>
                            )}
                        </td>
                    </tr>
                </tbody>
            </table>

            {
                worklistItems.length > 0 && (
                    <div style={{ backgroundColor: '#f5f5f5', borderRadius: 4, padding: 8, flex: 1 }}>
                        <h4>{t('Worklist')} ({worklistItems.length})</h4>
                        <table style={{ borderCollapse: 'collapse' }}>
                            <tbody>
                                {
                                    worklistItems.map((item, index) => (
                                        <tr style={{ marginRight: 8, marginBottom: 4 }} key={item.id}>
                                            <td style={{ padding: '4px 8px', borderBottom: '1px solid #ddd' }}>
                                                <Tag color="blue">{item.requestType?.displayName}</Tag>
                                            </td>
                                            <td style={{ padding: '4px 8px', borderBottom: '1px solid #ddd' }}>
                                                <Tag>{item?.accessionNumber}</Tag>
                                            </td>
                                            <td style={{ padding: '4px 8px', borderBottom: '1px solid #ddd' }}>
                                                {hasPermission(PERMISSIONS.WORKLIST_STATUS_CHANGE) && (
                                                    <Popconfirm
                                                        title={t('Are you sure you want to remove this item from the appointment?')}
                                                        onConfirm={() => handleRemove(item.id)}
                                                        okText={t('Yes')}
                                                        cancelText={t('No')}
                                                    >
                                                        <Button 
                                                            type="text" 
                                                            size="small" 
                                                            danger
                                                            icon={<DeleteOutlined />}
                                                        />
                                                    </Popconfirm>
                                                )}
                                            </td>
                                        </tr>
                                    ))
                                }
                            </tbody>
                        </table>
                    </div>
                )
            }
        </div>
    )
}